import React, { useEffect, useState } from "react";
import { Link, useHistory, Redirect } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
} from "@mui/material";
import { ArrowBack, Add, EventNote } from "@material-ui/icons";
import firebase from "../firebase";
import { palette } from "../theme";
import KidsForm from "../Components/KidsForm";
import PageContainer from "../Components/UI/PageContainer";
import PageHeader from "../Components/UI/PageHeader";

// Kid hub: details form + that kid's full session history side by side,
// so the OT never has to leave the page to see or log sessions.
export default function KidsProfile() {
  const history = useHistory();
  const signedIn = global.config.uid !== "";
  const kidID = new URLSearchParams(window.location.search).get("kidID") || "";
  const [sessions, setSessions] = useState([]);
  const [kidName, setKidName] = useState("");

  useEffect(() => {
    if (!signedIn || kidID === "") return;
    const db = firebase.firestore();
    const base = db.collection("Main").doc(global.config.uid);

    base
      .collection("Kids")
      .doc(kidID)
      .get()
      .then((doc) => {
        if (doc.exists) {
          setKidName(`${doc.data().FirstName || ""} ${doc.data().LastName || ""}`.trim());
        }
      });

    const unsub = base
      .collection("Sessions")
      .where("Kid", "==", kidID)
      .onSnapshot((snapshot) => {
        const list = snapshot.docs.map((doc) => ({
          id: doc.id,
          notes: doc.data().Notes,
          location: doc.data().Location,
          sessionType: doc.data().sessionType,
          date: doc.data().Date ? doc.data().Date.toDate() : new Date(0),
        }));
        list.sort((a, b) => b.date - a.date);
        setSessions(list);
      });
    return unsub;
  }, [signedIn, kidID]);

  if (!signedIn) return <Redirect to="/" />;

  return (
    <PageContainer>
      <Button
        component={Link}
        to="/kids"
        startIcon={<ArrowBack />}
        sx={{ mb: 1, color: palette.textSecondary }}
      >
        Back to Kids
      </Button>
      <PageHeader
        title={kidID ? kidName || "Kid Profile" : "Add a Kid"}
        subtitle={
          kidID
            ? `${sessions.length} ${sessions.length === 1 ? "session" : "sessions"} on record`
            : "Add a new kid to your caseload"
        }
        actions={
          kidID ? (
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Add />}
              onClick={() => history.push("/sessions-profile?kidID=" + kidID)}
            >
              Log Session
            </Button>
          ) : null
        }
      />

      <Grid container spacing={2.5}>
        <Grid item xs={12} md={kidID ? 6 : 8}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Details
              </Typography>
              <KidsForm />
            </CardContent>
          </Card>
        </Grid>

        {kidID && (
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">Session History</Typography>
                  <Button component={Link} to="/sessions" size="small">
                    All sessions
                  </Button>
                </Box>
                {sessions.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 5, color: palette.textSecondary }}>
                    <EventNote style={{ fontSize: 36, opacity: 0.4 }} />
                    <Typography sx={{ mt: 1, fontWeight: 600 }}>
                      No sessions yet for this kid
                    </Typography>
                    <Button
                      size="small"
                      startIcon={<Add />}
                      sx={{ mt: 1 }}
                      onClick={() => history.push("/sessions-profile?kidID=" + kidID)}
                    >
                      Log the first session
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ maxHeight: 520, overflowY: "auto", pr: 0.5 }}>
                    {sessions.map((s, i) => (
                      <Box
                        key={s.id}
                        component={Link}
                        to={"/sessions-profile?sessionID=" + s.id}
                        sx={{
                          display: "block",
                          textDecoration: "none",
                          color: palette.text,
                          borderRadius: "12px",
                          px: 1.5,
                          py: 1.25,
                          transition: "background-color 150ms ease",
                          "&:hover": {
                            backgroundColor: "#F0FAFC",
                            textDecoration: "none",
                            color: palette.text,
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 800 }}>
                            {s.date.toLocaleDateString(undefined, {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </Typography>
                          {s.location && (
                            <Chip
                              size="small"
                              label={s.location}
                              sx={{
                                backgroundColor: palette.primarySoft,
                                color: palette.primaryDark,
                                height: 22,
                              }}
                            />
                          )}
                          {s.sessionType === "GroupSession" && (
                            <Chip
                              size="small"
                              label="Group"
                              sx={{ backgroundColor: "#FEF3C7", color: "#92400E", height: 22 }}
                            />
                          )}
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ color: palette.textSecondary, whiteSpace: "pre-line" }}
                        >
                          {s.notes || "No notes"}
                        </Typography>
                        {i < sessions.length - 1 && <Divider sx={{ mt: 1.25 }} />}
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </PageContainer>
  );
}
